// components/AutocompleteInput.jsx
// Reusable autocomplete text input that fetches suggestions from a backend endpoint.

import React from 'react';
import axios from 'axios';
import { Z } from '../constants/tokens';

export default function AutocompleteInput({
  value,
  onChange,
  onClear,
  endpoint,       // e.g. '/autocomplete/places'
  backendUrl,
  placeholder = '',
  style = {},
  theme = {},
  queryParam = 'q', // query parameter name
  extraParams = {}, // e.g. { root: 'some_root' }
  minChars = 2,
  debounceMs = 250,
}) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [highlightIdx, setHighlightIdx] = React.useState(-1);
  const timerRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const reqIdRef = React.useRef(0);
  const optionRefs = React.useRef([]);
  const listId = React.useId();
  const optionId = (idx) => `${listId}-opt-${idx}`;

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = React.useCallback((query) => {
    // Sequence every fetch. Responses are not guaranteed to arrive in the order
    // they were sent, so a slow earlier request could otherwise land after a
    // newer one and repopulate the dropdown with suggestions for a query the
    // user has already typed past. Bumping the id here also covers the
    // too-short-query path below, so an in-flight response cannot undo a clear.
    const myReq = ++reqIdRef.current;
    if (!query || query.length < minChars) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    const params = new URLSearchParams({ [queryParam]: query, ...extraParams });
    axios.get(`${backendUrl}${endpoint}?${params.toString()}`)
      .then(r => {
        if (myReq !== reqIdRef.current) return; // superseded
        const items = r.data || [];
        setSuggestions(items);
        setShowDropdown(items.length > 0);
        setHighlightIdx(-1);
      })
      .catch(() => {
        if (myReq !== reqIdRef.current) return; // superseded
        setSuggestions([]);
        setShowDropdown(false);
      });
  }, [backendUrl, endpoint, queryParam, extraParams, minChars]);

  // Keep the arrow-key highlight visible inside the scrollable dropdown.
  React.useEffect(() => {
    if (highlightIdx < 0) return;
    optionRefs.current[highlightIdx]?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx]);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(e);
    // Debounced fetch
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(val), debounceMs);
  };

  const selectSuggestion = (item) => {
    // Simulate an onChange with the selected value
    onChange({ target: { value: item } });
    setShowDropdown(false);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIdx]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleFocus = () => {
    if (value && value.length >= minChars && suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        style={style}
        autoComplete="off"
        dir="auto"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={listId}
        aria-activedescendant={highlightIdx >= 0 ? optionId(highlightIdx) : undefined}
      />
      {showDropdown && suggestions.length > 0 && (
        <div role="listbox" id={listId} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: Z.dropdown,
          maxHeight: '180px', overflowY: 'auto',
          background: theme.card || '#222', border: `1px solid ${theme.border || '#444'}`,
          borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          marginTop: '2px',
        }}>
          {suggestions.map((item, idx) => {
            const isHighlighted = idx === highlightIdx;
            // Highlight the matching part of the suggestion
            const query = (value || '').toLowerCase();
            const matchIdx = item.toLowerCase().indexOf(query);
            let display;
            if (matchIdx >= 0 && query) {
              display = (
                <>
                  {item.slice(0, matchIdx)}
                  <strong style={{ color: theme.glossaryAccent || '#f5a623' }}>{item.slice(matchIdx, matchIdx + query.length)}</strong>
                  {item.slice(matchIdx + query.length)}
                </>
              );
            } else {
              display = item;
            }
            return (
              <div
                key={item}
                ref={el => { optionRefs.current[idx] = el; }}
                role="option"
                id={optionId(idx)}
                aria-selected={isHighlighted}
                dir="auto"
                onClick={() => selectSuggestion(item)}
                onMouseEnter={() => setHighlightIdx(idx)}
                style={{
                  padding: '10px 12px', fontSize: '0.8rem', cursor: 'pointer',
                  background: isHighlighted ? `${theme.glossaryAccent || '#f5a623'}33` : 'transparent',
                  borderLeft: `3px solid ${isHighlighted ? (theme.glossaryAccent || '#f5a623') : 'transparent'}`,
                  color: theme.text || '#fff',
                  borderBottom: idx < suggestions.length - 1 ? `1px solid ${theme.border || '#333'}22` : 'none',
                }}
              >
                {display}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
